import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, Loader2, MessageSquare, Send, Undo2 } from "lucide-react";
import { cn } from "@/utils/helpers";
import { kitButtonClass } from "../../components/kit/Button";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import { KitSelect, KitSelectItem } from "../../components/kit/Select";
import ConceptDocument, { type ConceptDocumentData } from "./ConceptDocument";
import {
  isApprovedDecision,
  latestSharedConceptVersion,
  orderedConceptVersions,
  sharedDiscussionFor,
  type ConceptReviewAudience,
  type ConceptReviewMessageVisibility,
  type ConceptReviewVersion,
} from "./conceptReviewModel";

function readableDate(value: string | null): string {
  if (!value) return "Draft";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function documentFromVersion(version: ConceptReviewVersion): ConceptDocumentData {
  return {
    title: version.title,
    synopsis: version.synopsis,
    hooks: version.hooks,
    objectiveClaim: version.objectiveClaim,
    pillarName: version.pillarName,
    durationSeconds: null,
    channels: [],
    reference: version.reference,
    aspectRatio: null,
  };
}

function readableTime(value: string): string {
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const secondaryButton = kitButtonClass({ variant: "secondary" });
const primaryButton = kitButtonClass({ variant: "primary" });
const iconButton = kitButtonClass({ variant: "primary", iconOnly: true });

function DecisionComposer({
  minimum,
  busy,
  error,
  onApprove,
  onRequestChanges,
}: {
  minimum: number;
  busy: boolean;
  error: string | null;
  onApprove: () => void;
  onRequestChanges: (reason: string) => void;
}) {
  const [requesting, setRequesting] = useState(false);
  const [reason, setReason] = useState("");
  const remaining = Math.max(0, minimum - reason.trim().length);

  if (!requesting) {
    return (
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2 rounded-panel bg-co-surface px-4 py-3 shadow-[inset_0_0_0_1px_var(--co-line)]">
        <button type="button" disabled={busy} onClick={() => setRequesting(true)} className={secondaryButton}>
          Ask for a change
        </button>
        <button type="button" disabled={busy} onClick={onApprove} className={primaryButton}>
          {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : <Check className="size-3.5" aria-hidden="true" />}
          Approve concept
        </button>
        {error ? <p className="tt-read-s basis-full text-co-alert-ink">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-panel bg-co-surface p-4 shadow-[inset_0_0_0_1px_var(--co-line)]">
      <label className="block">
        <span className="text-[12.5px] text-co-faint">What should shift?</span>
        <textarea
          autoFocus
          dir="auto"
          rows={4}
          maxLength={2000}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Keep the idea in view and describe the change the team should make."
          className="co-glass-control mt-2 w-full resize-y rounded-card px-3.5 py-3 text-[14px] leading-[1.45] text-co-text outline-none placeholder:text-co-faint focus:outline-2 focus:outline-co-accent"
        />
      </label>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="tt-read-s text-co-faint">
          {remaining > 0 ? `${remaining} more character${remaining === 1 ? "" : "s"}` : "Ready to send"}
        </span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={busy} onClick={() => setRequesting(false)} className={secondaryButton}>
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || remaining > 0}
            onClick={() => onRequestChanges(reason.trim())}
            className={primaryButton}
          >
            {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : <Undo2 className="size-3.5" aria-hidden="true" />}
            Send feedback
          </button>
        </div>
      </div>
      {error ? <p className="tt-read-s mt-2 text-co-alert-ink">{error}</p> : null}
    </div>
  );
}

function Discussion({
  version,
  audience,
  canComment,
  busy,
  error,
  lead,
  onAddComment,
}: {
  version: ConceptReviewVersion;
  audience: ConceptReviewAudience;
  canComment: boolean;
  busy: boolean;
  error: string | null;
  lead?: ReactNode;
  onAddComment?: (
    body: string,
    visibility?: ConceptReviewMessageVisibility,
  ) => void | Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const [visibility, setVisibility] = useState<ConceptReviewMessageVisibility>(
    audience === "team" ? "INTERNAL" : "SHARED",
  );
  const listRef = useRef<HTMLUListElement>(null);
  const messages = useMemo(
    () => sharedDiscussionFor(version, audience),
    [audience, version],
  );

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, version.id]);

  const submit = async () => {
    const body = draft.trim();
    if (busy || !body || !onAddComment) return;
    try {
      await onAddComment(body, visibility);
      setDraft("");
    } catch {
      // The caller owns the error copy. Keep the draft intact for a retry.
    }
  };

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-panel bg-co-surface shadow-[inset_0_0_0_1px_var(--co-line)] xl:max-h-[min(78vh,860px)]">
      <header className="flex min-h-12 items-center gap-2 border-b border-co-line px-4">
        <MessageSquare className="size-4 text-co-muted" strokeWidth={1.8} aria-hidden="true" />
        <h2 className="text-[14px] font-semibold text-co-text">Discussion</h2>
        <span className="ml-auto text-[12px] text-co-faint">V{version.version} · {messages.length}</span>
      </header>

      {lead ? <div className="border-b border-co-line p-4">{lead}</div> : null}

      {version.decision ? (
        <div className="border-b border-co-line2 bg-co-surface-solid px-4 py-4">
          <p className="text-[12px] text-co-faint">
            {isApprovedDecision(version.decision) ? "Client approved" : "Client feedback"} · {readableDate(version.decision.decidedAt)}
          </p>
          {version.decision.reason ? (
            <blockquote className="tt-read-m mt-2 border-l border-co-line2 pl-3 leading-relaxed text-co-text2">
              “{version.decision.reason}”
            </blockquote>
          ) : (
            <p className="tt-read-s mt-2 text-co-muted">{version.decision.clientName} approved this exact version.</p>
          )}
        </div>
      ) : null}

      {messages.length > 0 ? (
        <ul ref={listRef} className="custom-scrollbar flex min-h-[12rem] flex-1 flex-col overflow-y-auto">
          {messages.map((message) => (
            <li key={message.id} className="border-b border-co-line px-4 py-4 last:border-b-0">
              <div className="flex items-baseline gap-2">
                <span className="tt-read-s font-semibold text-co-text2">{message.authorName}</span>
                <span className="text-[12px] text-co-faint">
                  {message.visibility === "INTERNAL"
                    ? "Team only"
                    : message.authorType === "CLIENT"
                      ? "Client"
                      : "Mohtawa"}
                </span>
                <time className="ml-auto shrink-0 text-[12px] text-co-faint" dateTime={message.createdAt}>
                  {readableTime(message.createdAt)}
                </time>
              </div>
              <p className="tt-read-m mt-2 whitespace-pre-wrap break-words leading-relaxed text-co-muted">{message.body}</p>
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid min-h-[12rem] flex-1 place-items-center px-6 py-10 text-center">
          <p className="tt-read-m max-w-[28ch] text-co-faint">No shared discussion on this version yet.</p>
        </div>
      )}

      {canComment && onAddComment ? (
        <div className="border-t border-co-line2 p-3">
          {audience === "team" ? (
            <KitSegmentedControl
              className="mb-3"
              ariaLabel="Comment audience"
              variant="fill"
              value={visibility}
              onChange={setVisibility}
              options={[
                { value: "INTERNAL", label: "Team note" },
                { value: "SHARED", label: "Reply to client" },
              ]}
            />
          ) : null}
          <label className="sr-only" htmlFor={`concept-comment-${version.id}`}>Add a comment on version {version.version}</label>
          <div className="flex items-end gap-2">
            <textarea
              id={`concept-comment-${version.id}`}
              dir="auto"
              rows={3}
              maxLength={2000}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void submit();
                }
              }}
              placeholder={
                audience === "team" && visibility === "INTERNAL"
                  ? "Add a private note for the team"
                  : audience === "team"
                    ? "Reply to the client on this version"
                    : "Comment on this concept version"
              }
              className="co-glass-control min-w-0 flex-1 resize-y rounded-card px-3.5 py-2.5 text-[14px] leading-[1.45] text-co-text outline-none placeholder:text-co-faint focus:outline-2 focus:outline-co-accent"
            />
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !draft.trim()}
              aria-label="Post comment"
              className={cn(iconButton, "shrink-0")}
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : <Send className="size-3.5" aria-hidden="true" />}
            </button>
          </div>
          {error ? <p className="tt-read-s mt-2 text-co-alert-ink">{error}</p> : null}
        </div>
      ) : (
        <p className="border-t border-co-line px-4 py-3 text-[12px] text-co-faint">
          {audience === "preview" ? "Preview mode · responses disabled" : "This version is read-only"}
        </p>
      )}
    </aside>
  );
}

export interface ConceptReviewSurfaceProps {
  brandName: string;
  versions: ConceptReviewVersion[];
  selectedVersion: number;
  onVersionChange: (version: number) => void;
  audience: ConceptReviewAudience;
  canComment?: boolean;
  canDecide?: boolean;
  busy?: boolean;
  error?: string | null;
  commentError?: string | null;
  decisionError?: string | null;
  reasonMin?: number;
  discussionLead?: ReactNode;
  toolbarActions?: ReactNode;
  /**
   * The team's post page shows the discussion in the rail's Comments tab
   * (comments/PostComments), so the surface drops its own and keeps only the
   * slide; the lead moves above it.
   */
  hideDiscussion?: boolean;
  onAddComment?: (
    body: string,
    visibility?: ConceptReviewMessageVisibility,
  ) => void | Promise<void>;
  onApprove?: () => void;
  onRequestChanges?: (reason: string) => void;
}

/**
 * Shared review home for the team, preview, and public client portal. The 16:9
 * proposal slide remains the artifact; version-bound discussion sits beside it
 * on wide screens and below it when the viewport cannot support both.
 */
export default function ConceptReviewSurface({
  brandName,
  versions,
  selectedVersion,
  onVersionChange,
  audience,
  canComment = false,
  canDecide = false,
  busy = false,
  error = null,
  commentError,
  decisionError,
  reasonMin = 0,
  discussionLead,
  toolbarActions,
  hideDiscussion = false,
  onAddComment,
  onApprove,
  onRequestChanges,
}: ConceptReviewSurfaceProps) {
  const ordered = useMemo(() => orderedConceptVersions(versions), [versions]);
  const current = ordered.find((version) => version.version === selectedVersion) ?? ordered[0] ?? null;
  const latestShared = useMemo(() => latestSharedConceptVersion(versions), [versions]);

  if (!current) {
    return (
      <div className="rounded-panel bg-co-surface px-6 py-16 text-center shadow-[inset_0_0_0_1px_var(--co-line)]">
        <p className="tt-read-l text-co-muted">No concept version has been shared yet.</p>
      </div>
    );
  }

  const isLatestShared = latestShared?.id === current.id;
  const isDraft = current.sharedAt === null;
  const decision = current.decision;
  const approved = current.approved || isApprovedDecision(decision);
  const writeAllowed = audience !== "preview" && isLatestShared && !isDraft;
  const mayComment = writeAllowed && canComment && !approved;
  const mayDecide = writeAllowed && canDecide && !decision && !!onApprove && !!onRequestChanges;

  return (
    <section aria-label={`Concept version ${current.version}`} className="@container/review flex flex-col gap-4">
      <header className="flex flex-col gap-3 rounded-panel bg-co-surface px-4 py-3.5 shadow-[inset_0_0_0_1px_var(--co-line)] sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-co-faint">Concept review</p>
          <p className="mt-0.5 truncate text-[14px] font-semibold text-co-text">
            {isDraft ? `Version ${current.version} is a team draft.` : `Version ${current.version} was shared ${readableDate(current.sharedAt)}.`}
          </p>
        </div>
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 sm:justify-end">
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
            <span className="text-[12px] text-co-faint">Version</span>
            <KitSelect
              aria-label="Choose concept version"
              selectedKey={String(current.version)}
              onSelectionChange={(key) => {
                if (key != null) onVersionChange(Number(key));
              }}
              className="w-fit"
              buttonClassName="h-8 w-fit min-w-0 gap-1.5 px-3 text-[13px]"
            >
              {ordered.map((version) => {
                const label = `V${version.version}${version.id === latestShared?.id ? " · Current" : ""} · ${readableDate(version.sharedAt)}`;
                return (
                  <KitSelectItem key={version.id} id={String(version.version)} textValue={label}>
                    {label}
                  </KitSelectItem>
                );
              })}
            </KitSelect>
          </div>
          {toolbarActions}
        </div>
      </header>

      {hideDiscussion && discussionLead ? (
        <div className="rounded-panel bg-co-surface p-4 shadow-[inset_0_0_0_1px_var(--co-line)]">{discussionLead}</div>
      ) : null}

      <div
        className={
          hideDiscussion
            ? "min-w-0"
            : "grid min-w-0 gap-4 @[70rem]/review:grid-cols-[minmax(0,1fr)_22rem] @[70rem]/review:items-start"
        }
      >
        <div className="min-w-0">
          <ConceptDocument
            data={documentFromVersion(current)}
            audience={audience === "team" ? "team" : "client"}
            brandName={brandName}
          />

          {decision ? (
            <div className="mt-3 rounded-panel bg-co-surface px-4 py-4 shadow-[inset_0_0_0_1px_var(--co-line)]">
              <p className="text-[12px] text-co-faint">
                {isApprovedDecision(decision) ? "Approved" : "Changes requested"} by {decision.clientName} · {readableDate(decision.decidedAt)}
              </p>
              {/* The discussion held the client's words; without it they sit here. */}
              {hideDiscussion && decision.reason ? (
                <blockquote dir="auto" className="tt-read-m mt-2 whitespace-pre-wrap border-l border-co-line2 pl-3 leading-relaxed text-co-text2">
                  “{decision.reason}”
                </blockquote>
              ) : null}
            </div>
          ) : mayDecide ? (
            <DecisionComposer
              key={current.id}
              minimum={reasonMin}
              busy={busy}
              error={decisionError ?? error}
              onApprove={onApprove!}
              onRequestChanges={onRequestChanges!}
            />
          ) : !isLatestShared || isDraft ? (
            <p className="mt-3 rounded-panel bg-co-surface px-4 py-3 text-[12px] text-co-faint shadow-[inset_0_0_0_1px_var(--co-line)]">
              {isDraft ? "Team draft · not shared with the client" : "Past version · read-only"}
            </p>
          ) : null}
        </div>

        {hideDiscussion ? null : <Discussion
          key={current.id}
          version={current}
          audience={audience}
          canComment={mayComment}
          busy={busy}
          error={commentError ?? error}
          lead={discussionLead}
          onAddComment={onAddComment}
        />}
      </div>
    </section>
  );
}
